/**
 * TradeStack - Customer Form Page (Create/Edit)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../api';
import '../styles/Forms.css';

function CustomerForm() {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(isEditMode);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: '',
    altPhone: '',
    addressLine1: '',
    city: '',
    state: '',
    zip: '',
    notes: ''
  });

  useEffect(() => {
    if (isEditMode) {
      loadCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCustomer = async () => {
    try {
      const response = await api.getCustomer(id);
      const customer = response.data.customer;
      setFormData({
        firstName: customer.first_name || '',
        lastName: customer.last_name || '',
        companyName: customer.company_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        altPhone: customer.alt_phone || '',
        addressLine1: customer.address_line1 || '',
        city: customer.city || '',
        state: customer.state || '',
        zip: customer.zip || '',
        notes: customer.notes || ''
      });
    } catch (err) {
      setError('Failed to load customer');
      console.error(err);
    } finally {
      setLoadingCustomer(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditMode) {
        await api.updateCustomer(id, formData);
        navigate(`/customers/${id}`);
      } else {
        const response = await api.createCustomer(formData);
        navigate(`/customers/${response.data.customer.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} customer`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingCustomer) {
    return <div className="form-page"><div className="loading-state">Loading...</div></div>;
  }

  return (
    <div className="form-page">
      <div className="detail-header">
        <div className="header-left">
          <Link to={isEditMode ? `/customers/${id}` : '/customers'} className="back-link">
            &larr; {isEditMode ? 'Back to Customer' : 'Back to Customers'}
          </Link>
          <h1>{isEditMode ? 'Edit Customer' : 'New Customer'}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3>Contact Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="altPhone">Alt Phone</label>
            <input
              type="tel"
              id="altPhone"
              name="altPhone"
              value={formData.altPhone}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Address</h3>
          <div className="form-group">
            <label htmlFor="addressLine1">Street Address</label>
            <input
              type="text"
              id="addressLine1"
              name="addressLine1"
              value={formData.addressLine1}
              onChange={handleChange}
            />
          </div>

          <div className="form-row form-row-3">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="state">State</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                maxLength={2}
              />
            </div>
            <div className="form-group">
              <label htmlFor="zip">ZIP Code</label>
              <input
                type="text"
                id="zip"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Notes</h3>
          <div className="form-group">
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              placeholder="Any notes about this customer..."
            />
          </div>
        </div>

        <div className="form-actions">
          <Link to={isEditMode ? `/customers/${id}` : '/customers'} className="btn btn-secondary">Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Customer')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CustomerForm;
